import { Box, CircularProgress, SxProps } from "@mui/material";
import { ParameterGroup, Parameter } from "../../../common/types/parameters";
import {
  BAR_CHART_COLOR_DRIFT_UNDERHALL,
  BAR_CHART_COLOR_ENERGI,
  BAR_CHART_COLOR_EXTRA_REINVESTERING,
  BAR_CHART_COLOR_FJARRVARME_EFFEKT,
  BAR_CHART_COLOR_FLODESKOSTNAD,
  BAR_CHART_COLOR_KAPITALKOSTNAD,
} from "../../../components/data-presentation/barchart.colors";
import BarChart, {
  Orientation,
} from "../../../components/data-presentation/barchart";
import {
  SUPPLEMENTAL_DISTRICT_HEATING_LCC_COST_SUFFIXES,
  buildSupplementalHeatingTooltipMirrors,
} from "../../../components/data-presentation/barchart.supplemental-district-heating";

const costTypes: { id: string[]; name?: string }[] = [
  { id: ["GridLCCostPower"] },
  { id: ["GridLCCostFixed"] },
  { id: ["GridLCCostVariable"] },
  { id: ["GridElectricityTaxLCCost"] },
  { id: ["FlowLCCost"] },
  { id: ["LoadLCCost"] },
  { id: ["EnergyLCCost"] },
  { id: ["MaintenanceLCCost", "HeatPumpMaintenanceLCCost"] },
  { id: ["InvestmentLCCost"] },
  { id: ["ReinvestmentLCCost"] },
  { id: ["SupplementalDistrictHeatingTotalLCCostSum"] },
];

function getExpectedParameterId(
  costTypeId: string,
  heatingTechnologyId: string
): string {
  return `${heatingTechnologyId}${costTypeId}`;
}

function getParametersOfCostType(
  costTypeIds: string[],
  parameters: Parameter[],
  heatingTechnology: { id: string; title: string }
): Parameter[] {
  return costTypeIds
    .map((id) => getExpectedParameterId(id, heatingTechnology.id))
    .map((paramId) => parameters.find((parameter) => parameter.id === paramId))
    .filter((parameter) => parameter !== undefined) as Parameter[];
}

function resolveCostTypeDisplayNames(
  parameters: Parameter[],
  heatingTechnologies: { id: string; title: string }[]
): (string | undefined)[] {
  return costTypes.map(
    (costType) =>
      heatingTechnologies
        .flatMap((heatingTechnology) =>
          getParametersOfCostType(costType.id, parameters, heatingTechnology)
        )
        .find((param) => param.displayName)?.displayName
  );
}

function convertToParameterGroups(
  parameters: Parameter[],
  heatingTechnologies: { id: string; title: string; hidden?: boolean }[]
): ParameterGroup[] {
  const costTypeDisplayNames = resolveCostTypeDisplayNames(
    parameters,
    heatingTechnologies
  );
  let parameterGroups = heatingTechnologies.map(
    (heatingTechnology) =>
      ({
        id: heatingTechnology.id,
        displayName: heatingTechnology.title,
        description: "",
        hidden: heatingTechnology.hidden,
        parameters: costTypes.map(
          (costType, costTypeIndex) =>
            getParametersOfCostType(
              costType.id,
              parameters,
              heatingTechnology
            ).at(0) ??
            ({
              id: getExpectedParameterId(costType.id[0], heatingTechnology.id),
              value: "0",
              displayName: costTypeDisplayNames[costTypeIndex],
            } as Parameter)
        ),
      }) as ParameterGroup
  );
  return parameterGroups;
}

function getDistrictHeatingLccRowLabelsForMirror(
  parameterGroups: ParameterGroup[],
  parameters: Parameter[]
): string[] {
  const dhGroup = parameterGroups.find(
    (group) => group.id === "DistrictHeating"
  );
  return SUPPLEMENTAL_DISTRICT_HEATING_LCC_COST_SUFFIXES.map((suffix) => {
    const dhCostTypeIndex = costTypes.findIndex(
      (costType) => costType.id[0] === suffix
    );
    const labelFromDh =
      dhGroup?.parameters[dhCostTypeIndex]?.displayName?.trim();
    if (labelFromDh) {
      return labelFromDh;
    }
    const fromDhParam = parameters
      .find(
        (parameter) =>
          parameter.id === getExpectedParameterId(suffix, "DistrictHeating")
      )
      ?.displayName?.trim();
    if (fromDhParam) {
      return fromDhParam;
    }
    return suffix;
  });
}

export default function LifecycleChart(params: {
  heatingTechnologies: { id: string; title: string; hidden?: boolean }[];
  parameters: Parameter[];
  sx?: SxProps;
  displayLegends?: boolean;
  includeVat: boolean;
  toPrint: boolean;
}) {
  const chartParameterGroups = convertToParameterGroups(
    params.parameters,
    params.heatingTechnologies
  );
  const dhLabels = getDistrictHeatingLccRowLabelsForMirror(
    chartParameterGroups,
    params.parameters
  );
  const supplementalDistrictHeatingTooltipByCategory =
    buildSupplementalHeatingTooltipMirrors(
      params.parameters,
      params.heatingTechnologies,
      dhLabels,
      [...SUPPLEMENTAL_DISTRICT_HEATING_LCC_COST_SUFFIXES],
      [
        BAR_CHART_COLOR_KAPITALKOSTNAD,
        BAR_CHART_COLOR_DRIFT_UNDERHALL,
        BAR_CHART_COLOR_ENERGI,
        BAR_CHART_COLOR_FJARRVARME_EFFEKT,
        BAR_CHART_COLOR_FLODESKOSTNAD,
        BAR_CHART_COLOR_EXTRA_REINVESTERING,
      ]
    );

  return params.parameters.length > 0 ? (
    <BarChart
      description="Kostnader"
      parameterGroups={chartParameterGroups}
      unit="Kronor"
      orientation={Orientation.horizontal}
      displayLegends={params.displayLegends}
      includeVat={params.includeVat}
      toPrint={params.toPrint}
      supplementalDistrictHeatingTooltipByCategory={
        supplementalDistrictHeatingTooltipByCategory
      }
    />
  ) : (
    <Box sx={{ textAlign: "center", paddingTop: "50px" }}>
      <CircularProgress />
    </Box>
  );
}
